import React from 'react';
import { Card, Progress, Row, Col, Statistic, Tag } from 'antd';
import { BrandRating } from '../services/api';
import { useTranslation } from 'react-i18next';

interface RatingDisplayProps {
  rating: BrandRating;
}

const dimensionKeyMap: Record<string, string> = {
  environmental: 'rating_display.dimension_environmental',
  laborRights: 'rating_display.dimension_laborRights',
  animalWelfare: 'rating_display.dimension_animalWelfare',
  transparency: 'rating_display.dimension_transparency',
  socialResponsibility: 'rating_display.dimension_socialResponsibility',
  carbonFootprint: 'rating_display.dimension_carbonFootprint',
};

const RatingDisplay: React.FC<RatingDisplayProps> = ({ rating }) => {
  const { t } = useTranslation();
  const getScoreColor = (score: number) => {
    if (score >= 4) return '#52c41a';
    if (score >= 3) return '#faad14';
    return '#ff4d4f';
  };

  return (
    <div>
      <Card
        title={t('rating_display.overall_card')}
        style={{ marginBottom: 16 }}
      >
        <Row gutter={16} align="middle">
          <Col span={8}>
            <Statistic
              title={t('rating_display.total_score')}
              value={rating.overallScore}
              precision={1}
              suffix="/ 5"
              valueStyle={{ color: getScoreColor(rating.overallScore) }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title={t('rating_display.confidence')}
              value={rating.confidence * 100}
              precision={1}
              suffix="%"
            />
          </Col>
          <Col span={8}>
            <div>
              <div style={{ marginBottom: 8 }}>
                {t('rating_display.data_sources')}
              </div>
              {rating.dataSources.map(source => (
                <Tag key={source} color="geekblue">
                  {source}
                </Tag>
              ))}
            </div>
          </Col>
        </Row>
      </Card>

      <Card title={t('rating_display.dimensions_card')}>
        <Row gutter={[16, 16]}>
          {Object.entries(rating.dimensions).map(([key, dimension]) => {
            // 处理新的维度数据结构
            const score =
              typeof dimension === 'object' &&
              dimension !== null &&
              'score' in dimension
                ? dimension.score
                : typeof dimension === 'number'
                  ? dimension
                  : 0;
            const confidence =
              typeof dimension === 'object' &&
              dimension !== null &&
              'confidence' in dimension
                ? dimension.confidence
                : rating.confidence;

            return (
              <Col span={12} key={key}>
                <div style={{ marginBottom: 16 }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 8,
                    }}
                  >
                    <span>{t(dimensionKeyMap[key] || key)}</span>
                    <span>{score}/5</span>
                  </div>
                  <Progress
                    percent={(score / 5) * 100}
                    strokeColor={getScoreColor(score)}
                    showInfo={false}
                  />
                  <div
                    style={{ fontSize: '12px', color: '#666', marginTop: 4 }}
                  >
                    {t('rating_display.confidence')}:{' '}
                    {(confidence * 100).toFixed(1)}%
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
      </Card>
    </div>
  );
};

export default RatingDisplay;
